import { supabase } from './supabaseClient';
import { Aviso, HistorialAviso, AccionHistorial, MetodoPago, EstadoAviso } from '../types';
import { registrarPagoAviso } from './paymentService';
import { logAction } from './auditService';
import { Logger } from '../utils/logger';
import { sanitizeText, sanitizeHtml } from '../utils/sanitize';
import { sanitizePrice } from '../utils/sanitize';
import * as notificationsService from './notificationsService';
import * as resendEmailService from './resendEmailService';

// --- Functions related to Avisos ---

// --- Functions related to Avisos ---

// Standard fetching for public/users
export const getAvisos = async (): Promise<Aviso[]> => {
    try {
        const { data, error } = await supabase
            .from('avisos')
            .select(`
        *,
        usuario:usuarios(id_usuario, nombre, email, whatsapp, fecha_registro),
        categoria:categorias!avisos_id_categoria_fkey(id_categoria, nombre),
        subcategoria:categorias!avisos_id_subcategoria_fkey(id_categoria, nombre),
        plan:planes(id_plan, nombre, prioridad, resaltado),
        tienda:tiendas(id_tienda, nombre_tienda, logo_url, configuracion)
      `)
            .neq('estado', 'borrado')
            .order('fecha_creacion', { ascending: false });

        if (error) {
            Logger.error('ERROR in getAvisos', error);
            return [];
        }

        Logger.debug('getAvisos SUCCESS', { count: data?.length || 0 });

        return (data || []).map(aviso => {
            // Determine the correct WhatsApp number
            let whatsapp = null;
            const tienda = Array.isArray(aviso.tienda) ? aviso.tienda[0] : aviso.tienda;
            const usuario = Array.isArray(aviso.usuario) ? aviso.usuario[0] : aviso.usuario;

            if (aviso.id_tienda && tienda?.configuracion?.whatsapp?.enabled) {
                whatsapp = tienda.configuracion.whatsapp.numero;
            } else if (usuario?.whatsapp) {
                whatsapp = usuario.whatsapp;
            }

            return {
                ...aviso,
                whatsapp_cliente: whatsapp
            };
        });
    } catch (error) {
        Logger.error('Exception in getAvisos', error);
        return [];
    }
};

// Admin fetching - includes ALL statuses (deleted, suspended, etc.)
export const getAvisosAdmin = async (): Promise<Aviso[]> => {
    try {
        const { data, error } = await supabase
            .from('avisos')
            .select(`
        *,
        usuario:usuarios(id_usuario, nombre, email, whatsapp),
        categoria:categorias!avisos_id_categoria_fkey(id_categoria, nombre),
        subcategoria:categorias!avisos_id_subcategoria_fkey(id_categoria, nombre),
        plan:planes(id_plan, nombre, prioridad, resaltado)
      `)
            .order('fecha_creacion', { ascending: false });

        if (error) {
            Logger.error('Error fetching avisos admin', error);
            return [];
        }

        Logger.debug('getAvisosAdmin SUCCESS', { count: data?.length || 0 });

        return data || [];
    } catch (error) {
        Logger.error('Exception in getAvisosAdmin:', error);
        return [];
    }
};

// Optimized query for "Mis Avisos" section
export const getMisAvisos = async (userId: number): Promise<Aviso[]> => {
    try {
        const { data, error } = await supabase
            .from('avisos')
            .select(`
                *,
                categoria:categorias!avisos_id_categoria_fkey(id_categoria, nombre),
                subcategoria:categorias!avisos_id_subcategoria_fkey(id_categoria, nombre), 
                plan:planes(id_plan, nombre, prioridad, resaltado)
            `)
            .eq('id_usuario', userId)
            // Filter: Only active states (exclude 'borrado')
            .in('estado', ['pendiente', 'aprobado', 'rechazado', 'suspendido', 'vencido', 'expirado', 'borrador'])
            .order('fecha_creacion', { ascending: false });

        if (error) {
            Logger.error('Error fetching mis avisos', error);
            return [];
        }

        Logger.debug('getMisAvisos SUCCESS', { count: data?.length || 0 });
        return data || [];
    } catch (error: any) {
        Logger.error('Exception in getMisAvisos', error);
        return [];
    }
};

// Optimized query for list views - fetches only essential fields + first thumbnail
export const getAvisosLight = async (): Promise<any[]> => {
    try {
        const { data, error } = await supabase
            .from('avisos')
            .select(`
        id_aviso,
        titulo,
        precio,
        moneda,
        ubicacion,
        provincia,
        fecha_creacion,
        imagenes, 
        estado,
        destacado,
        urgente,
        id_categoria,
        id_usuario,
        id_tienda,
        tienda:tiendas(id_tienda, configuracion),
        usuario:usuarios(whatsapp)
      `)
            .eq('estado', 'aprobado')
            .order('destacado', { ascending: false }) // Featured first
            .order('fecha_creacion', { ascending: false });

        if (error) {
            Logger.error('Error fetching avisos light', error);
            return [];
        }

        Logger.debug('getAvisosLight SUCCESS', { count: data?.length || 0 });

        return (data || []).map(aviso => {
            // Determine the correct WhatsApp number
            let whatsapp = null;
            const tienda = Array.isArray(aviso.tienda) ? aviso.tienda[0] : aviso.tienda;
            const usuario = Array.isArray(aviso.usuario) ? aviso.usuario[0] : aviso.usuario;

            if (aviso.id_tienda && tienda?.configuracion?.whatsapp?.enabled) {
                whatsapp = tienda.configuracion.whatsapp.numero;
            } else if (usuario?.whatsapp) {
                whatsapp = usuario.whatsapp;
            }

            return {
                ...aviso,
                imagen_principal: aviso.imagenes && aviso.imagenes.length > 0 ? aviso.imagenes[0] : null,
                whatsapp_cliente: whatsapp
            };
        });
    } catch (error) {
        Logger.error('Exception in getAvisosLight', error);
        return [];
    }
};

// Optimized paginated query for list views
export const getAvisosLightPaginated = async (
    page: number = 1,
    limit: number = 10,
    filters?: {
        categoriaId?: number;
        subcategoriaId?: number;
        usuarioId?: number;
        estado?: string;
        busqueda?: string;
        provincia?: string;
        precioMin?: number;
        precioMax?: number;
        orderBy?: 'fecha' | 'precio' | 'vistas';
        orderDir?: 'asc' | 'desc';
    }
): Promise<{ data: any[], total: number, pages: number }> => {
    try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        let query = supabase
            .from('avisos')
            .select(`
          id_aviso,
          titulo,
          precio,
          moneda,
          ubicacion,
          provincia,
          fecha_creacion,
          imagenes, 
          estado,
          destacado,
          urgente,
          vistas,
          id_categoria,
          id_subcategoria,
          id_usuario,
          id_tienda,
          plan:planes(nombre, prioridad, resaltado),
          tienda:tiendas(id_tienda, configuracion),
          usuario:usuarios(whatsapp)
        `, { count: 'exact' });

        if (filters?.categoriaId) query = query.eq('id_categoria', filters.categoriaId);
        if (filters?.subcategoriaId) query = query.eq('id_subcategoria', filters.subcategoriaId);
        if (filters?.usuarioId) query = query.eq('id_usuario', filters.usuarioId);

        if (filters?.estado) {
            query = query.eq('estado', filters.estado);
        } else if (!filters?.usuarioId) {
            query = query.eq('estado', 'aprobado');
        }

        if (filters?.provincia) query = query.ilike('provincia', `%${filters.provincia}%`);

        if (filters?.busqueda) {
            query = query.or(`titulo.ilike.%${filters.busqueda}%,descripcion_detallada.ilike.%${filters.busqueda}%`);
        }

        if (filters?.precioMin !== undefined) query = query.gte('precio', filters.precioMin);
        if (filters?.precioMax !== undefined) query = query.lte('precio', filters.precioMax);

        let orderColumn = 'fecha_creacion';
        if (filters?.orderBy === 'precio') orderColumn = 'precio';
        if (filters?.orderBy === 'vistas') orderColumn = 'vistas';

        const ascending = filters?.orderDir === 'asc';

        query = query.order('urgente', { ascending: false });
        query = query.order('destacado', { ascending: false });
        query = query.order(orderColumn, { ascending });

        const { data, error, count } = await query.range(from, to);

        if (error) {
            Logger.error('Error fetching avisos paginated', error);
            return { data: [], total: 0, pages: 0 };
        }

        const formattedData = (data || []).map(aviso => {
            // Determine the correct WhatsApp number
            let whatsapp = null;
            const tienda = Array.isArray(aviso.tienda) ? aviso.tienda[0] : aviso.tienda;
            const usuario = Array.isArray(aviso.usuario) ? aviso.usuario[0] : aviso.usuario;

            if (aviso.id_tienda && tienda?.configuracion?.whatsapp?.enabled) {
                whatsapp = tienda.configuracion.whatsapp.numero;
            } else if (usuario?.whatsapp) {
                whatsapp = usuario.whatsapp;
            }

            return {
                ...aviso,
                imagen_principal: aviso.imagenes && aviso.imagenes.length > 0 ? aviso.imagenes[0] : null,
                whatsapp_cliente: whatsapp
            };
        });

        const total = count || 0;
        const pages = Math.ceil(total / limit);

        return { data: formattedData, total, pages };
    } catch (error) {
        Logger.error('Error in getAvisosLightPaginated', error);
        return { data: [], total: 0, pages: 0 };
    }
};

export const getAvisoById = async (id: number): Promise<Aviso | null> => {
    try {
        const { data, error } = await supabase
            .from('avisos')
            .select(`
        *,
            usuario: usuarios(id_usuario, nombre, email, whatsapp, fecha_registro),
                categoria: categorias!avisos_id_categoria_fkey(id_categoria, nombre),
                    subcategoria: categorias!avisos_id_subcategoria_fkey(id_categoria, nombre),
                        plan: planes(id_plan, nombre, prioridad, resaltado),
                            categoria_tienda: categorias_tienda(id_categoria_tienda, nombre),
                                tienda: tiendas(id_tienda, nombre_tienda, logo_url, configuracion)
      `)
            .eq('id_aviso', id)
            .single();

        if (error) return null;

        // Populate whatsapp_cliente
        let whatsapp = null;
        const tienda = Array.isArray(data.tienda) ? data.tienda[0] : data.tienda;
        const usuario = Array.isArray(data.usuario) ? data.usuario[0] : data.usuario;

        if (data.id_tienda && tienda?.configuracion?.whatsapp?.enabled) {
            whatsapp = tienda.configuracion.whatsapp.numero;
        } else if (usuario?.whatsapp) {
            whatsapp = usuario.whatsapp;
        }

        return {
            ...data,
            whatsapp_cliente: whatsapp
        };
    } catch (error) {
        Logger.error('Error in getAvisoById:', error);
        return null;
    }
};

export const createAviso = async (avisoData: Partial<Aviso>, metodo_pago?: MetodoPago, datos_pago?: any): Promise<Aviso> => {
    try {
        Logger.debug('Creating aviso', { titulo: avisoData.titulo });

        // Sanitize input: Remove fields that don't exist in the database table
        const {
            // Enriched/Frontend-only fields
            pais,
            stock,
            tienda_slug,
            nombre_tienda,
            nombre_cliente,
            nombre_usuario,
            nombre_categoria,
            nombre_subcategoria,
            nombre_plan,
            rol_usuario,
            whatsapp_cliente,
            telefono_usuario,
            whatsapp_usuario,
            prioridad,
            resaltado,
            variantes,
            analisis_fraude, // Also exclude this if not in DB
            puntuacion_fraude,
            requiere_revision_fraude,
            auto_renovar,
            fecha_fin_destacado,
            monto_destacado,
            fecha_fin_urgente,
            monto_urgente,
            // Objects/Arrays of relations
            categoria,
            subcategoria,
            plan,
            usuario,
            tienda,
            // AI Fields to exclude
            tags_sugeridos,
            original_index,
            error: aiError,
            condicion,
            // Keep the rest
            ...cleanData
        } = avisoData as any;

        const dataToInsert = {
            ...cleanData,
            // Sanitize user-controllable text fields (XSS protection)
            titulo: sanitizeText(cleanData.titulo || 'Sin título'),
            // NOTE: 'descripcion' column does not exist in avisos table - only descripcion_detallada
            descripcion_detallada: sanitizeHtml(
                cleanData.descripcion_detallada || cleanData.descripcion || '',
                ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'li', 'ol', 'a']
            ),
            ubicacion: sanitizeText(cleanData.ubicacion || ''),
            // Normalize precio: if null, undefined, or empty string, set to 0
            precio: cleanData.precio !== null && cleanData.precio !== undefined && cleanData.precio !== ''
                ? Number(cleanData.precio)
                : 0,
            // Default estado
            estado: cleanData.estado || 'pendiente',
            // Default fecha_expiracion
            fecha_expiracion: cleanData.fecha_expiracion || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            // Default moneda if not provided
            moneda: cleanData.moneda || 'ARS',
        };

        const { data, error } = await supabase
            .from('avisos')
            .insert(dataToInsert)
            .select()
            .single();

        if (error) {
            Logger.error('Supabase insert error', error);
            if (error.details) Logger.error('Error details:', error.details);
            if (error.hint) Logger.error('Error hint:', error.hint);
            throw new Error(`Error al crear el aviso: ${error.message} ${error.details ? `(${error.details})` : ''} `);
        }

        if (!data) throw new Error("No se recibieron datos del aviso creado");

        Logger.debug('Aviso created successfully', { id_aviso: data.id_aviso });

        if (metodo_pago && datos_pago) {
            await registrarPagoAviso(data.id_aviso, datos_pago, avisoData.id_plan, avisoData.id_usuario, datos_pago.monto);
            const updatedAviso = await getAvisoById(data.id_aviso);
            return updatedAviso || data;
        }

        const userId = avisoData.id_usuario || 0;
        await registrarAccion(data.id_aviso, userId, AccionHistorial.Creacion, 'Aviso creado');
        await logAction('CREATE_AVISO', { id_aviso: data.id_aviso, titulo: data.titulo });

        return data;
    } catch (error: any) {
        Logger.error('Exception in createAviso', error);
        throw error;
    }
};

export const updateAviso = async (id: number, updates: Partial<Aviso>, actorId?: number): Promise<Aviso> => {
    try {
        // Strip any fields that are NOT actual database columns
        // These come from Supabase JOIN aliases in SELECT queries
        const {
            categoria,
            subcategoria,
            plan,
            usuario,
            nombre_categoria,
            nombre_subcategoria,
            nombre_plan,
            tienda,
            variantes,
            // AI Fields to exclude
            tags_sugeridos,
            original_index,
            error: aiError,
            condicion,
            ...cleanUpdates
        } = updates as any;

        // Normalize fields if they are being updated
        const normalizedUpdates: any = { ...cleanUpdates };

        // Sanitize user-controllable text fields (XSS protection)
        if ('titulo' in normalizedUpdates) {
            normalizedUpdates.titulo = sanitizeText(normalizedUpdates.titulo || 'Sin título');
        }

        if ('descripcion' in normalizedUpdates) {
            normalizedUpdates.descripcion = sanitizeText(normalizedUpdates.descripcion || '');
        }

        if ('descripcion_detallada' in normalizedUpdates) {
            normalizedUpdates.descripcion_detallada = sanitizeHtml(
                normalizedUpdates.descripcion_detallada || '',
                ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'li', 'ol', 'a']
            );
        }

        if ('ubicacion' in normalizedUpdates) {
            normalizedUpdates.ubicacion = sanitizeText(normalizedUpdates.ubicacion || '');
        }

        // If precio is being updated and is null/undefined/empty, set to 0
        if ('precio' in normalizedUpdates) {
            normalizedUpdates.precio = normalizedUpdates.precio !== null && normalizedUpdates.precio !== undefined && normalizedUpdates.precio !== ''
                ? Number(normalizedUpdates.precio)
                : 0;
        }

        const { data, error } = await supabase
            .from('avisos')
            .update(normalizedUpdates)
            .eq('id_aviso', id)
            .select(`
            *,
            usuario: usuarios(nombre, email)
            `)
            .single();

        if (error) throw error;


        const userId = actorId || updates.id_usuario || 0;
        await registrarAccion(id, userId, AccionHistorial.EdicionContenido, 'Aviso actualizado');
        await logAction('UPDATE_AVISO', { id_aviso: id, changes: Object.keys(cleanUpdates) });

        // Log status change (Admin moderation) & Notify User
        if (cleanUpdates.estado) {
            await logAction('CHANGE_AVISO_STATUS', { id_aviso: id, new_status: cleanUpdates.estado });

            // --- Notification Logic ---
            if (data && data.usuario) {
                // Fix: Ensure usuario is treated as object, not array (Supabase standard join)
                const user = Array.isArray(data.usuario) ? data.usuario[0] : data.usuario;

                if (cleanUpdates.estado === 'aprobado') {
                    // 1. In-App
                    await notificationsService.notifyAvisoApproved(data.id_usuario, data.id_aviso, data.titulo);
                    // 2. Email
                    await resendEmailService.sendAvisoApprovedEmail(user, data);
                } else if (cleanUpdates.estado === 'rechazado') {
                    // 1. In-App
                    await notificationsService.notifyAvisoRejected(data.id_usuario, data.id_aviso, data.titulo, cleanUpdates.razon_rechazo);
                    // 2. Email
                    await resendEmailService.sendAvisoRejectedEmail(user, data, (updates as any).razon_rechazo);
                }
            }
            // ---------------------------
        }

        return data;
    } catch (error) {
        Logger.error('Error updating aviso:', error);
        throw error;
    }
};

export const republicarAviso = async (id: number): Promise<Aviso> => {
    try {
        const now = new Date().toISOString();
        const expiration = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

        const { data, error } = await supabase
            .from('avisos')
            .update({
                fecha_creacion: now,
                fecha_expiracion: expiration,
                estado: EstadoAviso.Aprobado
            })
            .eq('id_aviso', id)
            .select()
            .single();

        if (error) throw error;

        if (data) {
            await registrarAccion(id, data.id_usuario, AccionHistorial.Reanudacion, 'Aviso republicado');
            await logAction('REPUBLISH_AVISO', { id_aviso: id });
        }

        return data;
    } catch (error) {
        Logger.error('Error republicando aviso:', error);
        throw error;
    }
};

export const deleteAviso = async (id: number, hardDelete: boolean = false): Promise<boolean> => {
    try {
        if (hardDelete) {
            const { error } = await supabase
                .from('avisos')
                .delete()
                .eq('id_aviso', id);

            if (error) throw error;
        } else {
            const { data, error } = await supabase
                .from('avisos')
                .update({ estado: 'borrado' })
                .eq('id_aviso', id)
                .select();

            if (error) throw error;
            if (!data || data.length === 0) {
                throw new Error("No se pudo eliminar el aviso. Verifica tus permisos o intenta recargar la página.");
            }
        }

        return true;
    } catch (error) {
        Logger.error('Error deleting aviso:', error);
        throw error;
    }
};

export const createBatchDrafts = async (
    avisosData: Partial<Aviso>[],
    id_usuario: number,
    id_plan: number,
    id_tienda?: number
): Promise<{ success: boolean; count: number; error?: string }> => {
    try {
        // CRITICAL: Enforce max 20 items to prevent API/server saturation
        if (avisosData.length > 20) {
            return {
                success: false,
                count: 0,
                error: `Límite excedido: máximo 20 productos por importación(recibidos: ${avisosData.length})`
            };
        }

        const batch = avisosData.map((aviso: any) => ({
            // Sanitize user-controllable fields (XSS protection)
            titulo: sanitizeText(aviso.titulo || 'Sin título'),
            // NOTE: 'descripcion' column does not exist - use descripcion_detallada instead
            descripcion_detallada: sanitizeHtml(
                aviso.descripcion_detallada || aviso.descripcion || '',
                ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'li', 'ol']
            ),
            ubicacion: sanitizeText(aviso.ubicacion || ''),
            precio: aviso.precio !== null && aviso.precio !== undefined && aviso.precio !== ''
                ? Number(aviso.precio)
                : 0,
            moneda: aviso.moneda || 'ARS',
            id_categoria: aviso.id_categoria, // Mapped in frontend (can be null for flat catalog)
            id_categoria_tienda: aviso.id_categoria_tienda, // Mapped in frontend
            stock: aviso.stock || 1,
            // Standard fields
            id_usuario,
            id_plan,
            id_tienda: id_tienda || null, // FIX: Assign to store
            estado: 'borrador',
            puntuacion_fraude: aviso.puntuacion_fraude || 0,
            requiere_revision_fraude: aviso.requiere_revision_fraude || false,
            fecha_expiracion: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            pagado: false,
            fecha_creacion: new Date().toISOString()
        }));

        const { data, error } = await supabase
            .from('avisos')
            .insert(batch)
            .select();

        if (error) throw error;

        return { success: true, count: data?.length || 0 };
    } catch (error: any) {
        Logger.error("Batch creation error:", error);
        return { success: false, count: 0, error: error.message };
    }
};

export const actualizarAvisosExpirados = async (): Promise<void> => {
    try {
        const { error } = await supabase
            .rpc('marcar_avisos_expirados');

        if (error) {
            // console.debug("RPC marcar_avisos_expirados not found, falling back to client-side update");

            const now = new Date().toISOString();
            const { error: updateError } = await supabase
                .from('avisos')
                .update({ estado: 'expirado' })
                .lt('fecha_expiracion', now)
                .in('estado', ['aprobado', 'pendiente']);

            if (updateError) console.error("Error updating expired ads:", updateError);
        }
    } catch (error) {
        console.error("Error in actualizarAvisosExpirados:", error);
    }
};

export const registrarAccion = async (
    id_aviso: number,
    id_usuario_accion: number,
    accion: AccionHistorial,
    detalles: string | null = null
): Promise<void> => {
    try {
        await supabase
            .from('historial_avisos')
            .insert({
                id_aviso,
                id_usuario_accion,
                accion,
                detalles
            });
    } catch (error) {
        console.error('Error logging history:', error);
    }
};

export const getHistorialPorAviso = async (id_aviso: number): Promise<HistorialAviso[]> => {
    try {
        const { data, error } = await supabase
            .from('historial_avisos')
            .select(`
            *,
            usuario: usuarios(nombre, email)
      `)
            .eq('id_aviso', id_aviso)
            .order('fecha_accion', { ascending: false });

        if (error) {
            console.error('Error fetching historial:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Error in getHistorialPorAviso:', error);
        return [];
    }
};

export const incrementarVistas = async (id_aviso: number): Promise<number> => {
    try {
        const { data, error } = await supabase
            .rpc('increment_views', { aviso_id: id_aviso });

        if (error) return 0;

        return data;
    } catch (error) {
        console.error('Error in incrementarVistas:', error);
        return 0;
    }
};

export const destacarAviso = async (
    id_aviso: number,
    id_usuario: number,
    dias: number = 7,
    monto: number,
    datos_pago?: any
): Promise<{ success: boolean; fecha_fin: string }> => {
    try {
        const fechaFin = new Date();
        fechaFin.setDate(fechaFin.getDate() + dias);

        if (monto > 0) {
            const { error: pagoError } = await supabase.from('pagos').insert({
                id_usuario,
                id_plan: 1,
                monto,
                estado: 'completado',
                metodo_pago: 'mercadopago',
                fecha_pago: new Date().toISOString()
            });
            if (pagoError) console.warn("Payment log failed but proceeding with boost:", pagoError);
        }

        const { error: avisoError } = await supabase
            .from('avisos')
            .update({
                destacado: true
            })
            .eq('id_aviso', id_aviso);

        if (avisoError) throw avisoError;

        await registrarAccion(id_aviso, id_usuario, AccionHistorial.EdicionContenido, `Aviso destacado por ${dias} días`);
        await logAction('BOOST_AVISO', { id_aviso: id_aviso, dias, monto });

        return { success: true, fecha_fin: fechaFin.toISOString() };

    } catch (error) {
        console.error("Error highlighting aviso:", error);
        return { success: false, fecha_fin: "" };
    }
};

export const marcarAvisoUrgente = async (
    id_aviso: number,
    id_usuario: number,
    dias: number = 3,
    monto: number,
    datos_pago?: any
): Promise<{ success: boolean; fecha_fin: string }> => {
    try {
        const fechaFin = new Date();
        fechaFin.setDate(fechaFin.getDate() + dias);

        if (monto > 0) {
            await supabase.from('pagos').insert({
                id_usuario,
                id_plan: 1,
                monto,
                estado: 'completado',
                metodo_pago: 'mercadopago',
                fecha_pago: new Date().toISOString()
            });
        }

        const { error: avisoError } = await supabase
            .from('avisos')
            .update({
                urgente: true
            })
            .eq('id_aviso', id_aviso);

        if (avisoError) throw avisoError;

        await registrarAccion(id_aviso, id_usuario, AccionHistorial.EdicionContenido, `Aviso marcado urgente por ${dias} días`);
        await logAction('URGENT_AVISO', { id_aviso: id_aviso, dias, monto });

        return { success: true, fecha_fin: fechaFin.toISOString() };

    } catch (error) {
        console.error("Error marking aviso as urgent:", error);
        return { success: false, fecha_fin: "" };
    }
};

// --- Product Variants Management ---

export const createVariante = async (varianteData: any): Promise<any> => {
    try {
        const { data, error } = await supabase
            .from('producto_variantes')
            .insert(varianteData)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error creating variante:', error);
        throw error;
    }
};

export const updateVariante = async (id: number, updates: any): Promise<any> => {
    try {
        const { data, error } = await supabase
            .from('producto_variantes')
            .update(updates)
            .eq('id_variante', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating variante:', error);
        throw error;
    }
};

export const deleteVariante = async (id: number): Promise<{ success: boolean }> => {
    try {
        const { error } = await supabase
            .from('producto_variantes')
            .delete()
            .eq('id_variante', id);

        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error deleting variante:', error);
        return { success: false };
    }
};

export const avisoService = {
    getAvisos,
    getAvisosAdmin,
    getMisAvisos,
    getAvisosLight,
    getAvisosLightPaginated,
    getAvisoById,
    createAviso,
    updateAviso,
    republicarAviso,
    deleteAviso,
    createBatchDrafts,
    actualizarAvisosExpirados,
    registrarAccion,
    getHistorialPorAviso,
    incrementarVistas,
    destacarAviso,
    marcarAvisoUrgente,
    createVariante,
    updateVariante,
    deleteVariante
};

export default avisoService;
